import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {Group, Point} from '@postermywall/fabricjs-2';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import {getClusterBounds, getSelectionClusters, moveCluster} from '@PosterWhiteboard/page/selection-clusters.library';

const ALIGNMENT_PADDING = 0;

export enum GroupVeticalAlignment {
  TOP = 'top',
  BOTTOM = 'bottom',
  CENTER = 'center',
}

export enum GroupHorizontalAlignment {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export enum AlignType {
  CENTER = 'center',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export class Alignment {
  public page: Page;

  public constructor(page: Page) {
    this.page = page;
  }

  public horizontalAlignment(horizontalAlignmentType: GroupHorizontalAlignment): void {
    const fabricObjects = this.page.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const group = this.page.activeSelection.getActiveObject();
    if (!(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    this.#alignClustersHorizontally(group, this.#getAlignmentUnits(), horizontalAlignmentType);
    this.page.fabricCanvas.requestRenderAll();
  }

  public verticalAlignment(verticalAlignmentType: GroupVeticalAlignment): void {
    const fabricObjects = this.page.activeSelection.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const group = this.page.activeSelection.getActiveObject();
    if (!(group instanceof Group)) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    this.#alignClustersVertically(group, this.#getAlignmentUnits(), verticalAlignmentType);
    this.page.fabricCanvas.requestRenderAll();
  }

  /**
   * The rigid units the within-selection alignment operates on. Saved groups in a mixed
   * selection align as one unit each, so their internal layout survives; a single saved group
   * selected alone aligns its members individually instead — the deliberate "align items
   * within a group" behaviour.
   */
  #getAlignmentUnits(): ItemType[][] {
    const clusters = getSelectionClusters(this.page);
    if (clusters.length > 1) {
      return clusters;
    }
    return this.page.getSelectedItems().map((item) => {
      return [item];
    });
  }

  /**
   * Aligns clusters as rigid units: every cluster moves by one shared delta toward the
   * selection edge or center given by `type`.
   */
  #alignClustersHorizontally(group: Group, clusters: ItemType[][], type: GroupHorizontalAlignment): void {
    for (const cluster of clusters) {
      const bounds = getClusterBounds(cluster);
      let deltaX: number;
      switch (type) {
        case GroupHorizontalAlignment.LEFT:
          deltaX = -group.width / 2 + group.padding - bounds.minX;
          break;
        case GroupHorizontalAlignment.RIGHT:
          deltaX = group.width / 2 - group.padding - bounds.maxX;
          break;
        case GroupHorizontalAlignment.CENTER:
          deltaX = -(bounds.minX + bounds.maxX) / 2;
          break;
        default:
          return;
      }
      moveCluster(cluster, deltaX, 0);
    }
    commitPosterMutation(this.page);
  }

  #alignClustersVertically(group: Group, clusters: ItemType[][], type: GroupVeticalAlignment): void {
    for (const cluster of clusters) {
      const bounds = getClusterBounds(cluster);
      let deltaY: number;
      switch (type) {
        case GroupVeticalAlignment.TOP:
          deltaY = -group.height / 2 + group.padding - bounds.minY;
          break;
        case GroupVeticalAlignment.BOTTOM:
          deltaY = group.height / 2 - group.padding - bounds.maxY;
          break;
        case GroupVeticalAlignment.CENTER:
          deltaY = -(bounds.minY + bounds.maxY) / 2;
          break;
        default:
          return;
      }
      moveCluster(cluster, 0, deltaY);
    }
    commitPosterMutation(this.page);
  }

  public alignItems(type: AlignType, undoable = true, fireModifiedEvent = true): void {
    const activeFabricObject = this.page.activeSelection.getActiveObject();
    if (!activeFabricObject) {
      return;
    }

    if (activeFabricObject.lockMovementX) {
      return;
    }

    const item = this.page.items.getItemForFabricObject(activeFabricObject);
    if (item && (item.isText() || item.isSlideshow())) {
      item.restoreDefaultState();
    }

    const items = this.page.getSelectedItems();
    const isGroup = items.length > 1;
    const canvasCenter = this.page.fabricCanvas.getCenterPoint();
    const viewCenter = activeFabricObject.getCenterPoint();
    const point = new Point({
      x: 0,
      y: 0,
    });

    switch (type) {
      case AlignType.VERTICAL:
        point.x = viewCenter.x;
        point.y = canvasCenter.y / this.page.poster.scaling.scale;
        break;
      case AlignType.HORIZONTAL:
        point.x = canvasCenter.x / this.page.poster.scaling.scale;
        point.y = viewCenter.y;
        break;
      case AlignType.CENTER:
        point.x = canvasCenter.x / this.page.poster.scaling.scale;
        point.y = canvasCenter.y / this.page.poster.scaling.scale;
        break;
      case AlignType.LEFT: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const minX = Math.min(c.tl.x, c.tr.x, c.bl.x, c.br.x);
        point.x = viewCenter.x - minX + ALIGNMENT_PADDING;
        point.y = viewCenter.y;
        break;
      }
      case AlignType.RIGHT: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const maxX = Math.max(c.tl.x, c.tr.x, c.bl.x, c.br.x);
        const canvasWidth = this.page.poster.width;
        point.x = viewCenter.x + canvasWidth - maxX - ALIGNMENT_PADDING;
        point.y = viewCenter.y;
        break;
      }
      case AlignType.TOP: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const minY = Math.min(c.tl.y, c.tr.y, c.bl.y, c.br.y);
        point.x = viewCenter.x;
        point.y = viewCenter.y - minY + ALIGNMENT_PADDING;
        break;
      }
      case AlignType.BOTTOM: {
        const c = activeFabricObject.getCornerPoints(viewCenter);
        const maxY = Math.max(c.tl.y, c.tr.y, c.bl.y, c.br.y);
        const canvasHeight = this.page.poster.height;
        point.x = viewCenter.x;
        point.y = viewCenter.y + canvasHeight - maxY - ALIGNMENT_PADDING;
        break;
      }
      default:
        return;
    }

    const corner = activeFabricObject.getCornerPoints(point);
    // In case of group we are using fabric.object set() function to move the group on canvas as for now we do not
    // have a group mediator
    if (isGroup) {
      activeFabricObject.set({
        left: Math.round(corner.tl.x),
        top: Math.round(corner.tl.y),
      });
      activeFabricObject.setCoords();

      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      if (fireModifiedEvent) {
        this.page.fire('modified');
      }
      this.page.poster.redux.updateReduxData();
      this.page.fabricCanvas.requestRenderAll();
    } else {
      void items[0].updateFromObject(
        {
          left: Math.round(corner.tl.x),
          top: Math.round(corner.tl.y),
        },
        {undoable, fireModifiedEvent}
      );
    }
  }
}
